import React from 'react';
import unionBy from 'lodash/unionBy';

import { setIn } from '@lyearn/core/utils/immutable';
import useTagsFilter from '@/components/FilterManager/Tags/TagsFilter/useTagsFilter';
import AttachTagsComp from '@/modules/corporate/modules/tags/components/AttachTags';
import {
  SelectedTagValues,
  TagInputProps,
  TagsMenuItem,
} from '@/modules/corporate/modules/tags/types';
import { TagAvailableOnEnum } from '@/types/schema';

import { getFilteredTagsAndTagValuesByWorkspace } from '../../../../modules/corporate/modules/tags/utils';

interface AttachTagsProps {
  value: TagInputProps;
  setFieldValue: any;
  availableOn?: TagAvailableOnEnum[];
  subWorkspaceIds?: string[];
  error?: string;
  readonly?: boolean;
}

// We assume the fieldkey will be 'tags' alwayssss
function AttachTags({
  value,
  setFieldValue,
  availableOn,
  error,
  subWorkspaceIds,
  readonly,
}: AttachTagsProps) {
  // need to maintain this state to retrieve tags in useEffect
  const [tags, setTags] = React.useState<TagInputProps>(value);

  const { filterGroups } = useTagsFilter({
    availableOn,
    requiredTag: true,
    subWorkspaceIds,
  });

  React.useEffect(() => {
    setTags((prevValue) => {
      const requiredItems = filterGroups?.[0]?.options as TagsMenuItem[];
      const resolvedTags = unionBy(prevValue?.tags, requiredItems, 'id');

      const { tagValues: filteredTagValues, tags: filteredTags } =
        getFilteredTagsAndTagValuesByWorkspace({
          tags: resolvedTags,
          tagValues: prevValue?.tagValues,
          subWorkspaceIds,
        });

      setFieldValue('tags.tags', filteredTags, true);
      setFieldValue('tags.tagValues', filteredTagValues, true);

      return {
        tags: filteredTags,
        tagValues: filteredTagValues,
      };
    });
  }, [filterGroups, setFieldValue, subWorkspaceIds]);

  const onTagChange = React.useCallback(
    (nextValue: TagsMenuItem[]) => {
      setFieldValue('tags.tags', nextValue, true);
      setTags((prevValue) => {
        return setIn(prevValue || {}, 'tags', nextValue);
      });
    },
    [setFieldValue],
  );

  const onTagsValueChange = React.useCallback(
    (nextValue: SelectedTagValues) => {
      setFieldValue('tags.tagValues', nextValue, true);
      setTags((prevValue) => {
        return setIn(prevValue || {}, 'tagValues', nextValue);
      });
    },
    [setFieldValue],
  );

  const onRemoveTag = React.useCallback(
    (tagId: string) => {
      const filteredTags = tags?.tags?.filter((tag) => tag.id !== tagId);
      filteredTags && onTagChange(filteredTags);
    },
    [tags, onTagChange],
  );

  return (
    <AttachTagsComp
      availableOn={availableOn}
      error={error}
      readonly={readonly}
      selectedTagValues={tags?.tagValues}
      subWorkspaceIds={subWorkspaceIds}
      tags={tags?.tags}
      onRemoveTag={onRemoveTag}
      onTagChange={onTagChange}
      onTagsValueChange={onTagsValueChange}
    />
  );
}

export default React.memo(AttachTags);
